import ReactMarkdown from 'react-markdown';
import type { ResponseDetail, ResponseSource } from '../types/index.js';
import { EngineBadge } from './EngineBadge.js';
import { AioResponseBody, type AioSource } from './AioResponseBody.js';
import {
  CaptureModeBadge,
  SearchStatusLine,
  ExtractionDiscrepancyWarning,
} from './CaptureProvenance.js';
import { ResponseRegionBadge } from './RegionLabels.js';

interface ResponseContentProps {
  response: ResponseDetail;
}

/**
 * Domain type to Tailwind color class map for citation badges.
 * Exported so spec 094's AioResponseBody can reuse the same visual vocabulary
 * (no new design tokens — see plan.md SC-5).
 */
export const DOMAIN_TYPE_COLORS: Record<string, { bg: string; text: string }> = {
  your: { bg: 'bg-green-100', text: 'text-green-800' },
  competitor: { bg: 'bg-red-100', text: 'text-red-800' },
  editorial: { bg: 'bg-blue-100', text: 'text-blue-800' },
  institutional: { bg: 'bg-purple-100', text: 'text-purple-800' },
  corporate: { bg: 'bg-amber-100', text: 'text-amber-800' },
  reference: { bg: 'bg-gray-200', text: 'text-gray-700' },
  ugc: { bg: 'bg-pink-100', text: 'text-pink-800' },
  other: { bg: 'bg-gray-100', text: 'text-gray-600' },
};

export function getDomainTypeColor(type: string | null) {
  return DOMAIN_TYPE_COLORS[type ?? 'other'] ?? DOMAIN_TYPE_COLORS.other;
}

/**
 * Build a list of citation markers found in the response text.
 * Each marker has its position in the text, length, and source info.
 */
function findCitationPositions(
  text: string,
  sources: ResponseSource[],
): Array<{ start: number; end: number; source: ResponseSource }> {
  const markers: Array<{ start: number; end: number; source: ResponseSource }> = [];

  for (const source of sources) {
    if (!source.is_cited) continue;

    // Try to find domain name or URL in the text
    const searchTerms: string[] = [];
    if (source.domain) searchTerms.push(source.domain);
    if (source.url) {
      // Also try the URL path portion
      try {
        const url = new URL(source.url);
        searchTerms.push(url.hostname);
      } catch {
        // Not a valid URL, skip
      }
      searchTerms.push(source.url);
    }
    if (source.domain_display_name) searchTerms.push(source.domain_display_name);

    for (const term of searchTerms) {
      if (!term || term.length < 3) continue;
      const idx = text.toLowerCase().indexOf(term.toLowerCase());
      if (idx !== -1) {
        // Check no overlap with existing markers
        const overlaps = markers.some(
          (m) => (idx >= m.start && idx < m.end) || (idx + term.length > m.start && idx + term.length <= m.end),
        );
        if (!overlaps) {
          markers.push({ start: idx, end: idx + term.length, source });
          break; // Only first match per source
        }
      }
    }
  }

  // Sort by position
  markers.sort((a, b) => a.start - b.start);
  return markers;
}

/** Render response text as Markdown with Tailwind prose-like styling. */
function MarkdownResponse({ text }: { text: string }) {
  return (
    <div className="prose-sm text-sm leading-relaxed text-slate-800 [&_h1]:text-lg [&_h1]:font-semibold [&_h1]:text-slate-900 [&_h1]:mt-4 [&_h1]:mb-2 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:text-slate-900 [&_h2]:mt-3 [&_h2]:mb-1.5 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-slate-900 [&_h3]:mt-2 [&_h3]:mb-1 [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2 [&_li]:mb-0.5 [&_strong]:font-semibold [&_strong]:text-slate-900 [&_code]:rounded [&_code]:bg-slate-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-xs [&_code]:font-mono [&_blockquote]:border-l-2 [&_blockquote]:border-slate-300 [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:text-slate-600 [&_a]:text-violet-600 [&_a]:underline">
      <ReactMarkdown>{text}</ReactMarkdown>
    </div>
  );
}

/**
 * Render response text with inline citation badges.
 */
function HighlightedText({ text, sources }: { text: string; sources: ResponseSource[] }) {
  const markers = findCitationPositions(text, sources);

  if (markers.length === 0) {
    return <MarkdownResponse text={text} />;
  }

  const parts: Array<{ text: string; source?: ResponseSource }> = [];
  let lastEnd = 0;

  for (const marker of markers) {
    if (marker.start > lastEnd) {
      parts.push({ text: text.slice(lastEnd, marker.start) });
    }
    parts.push({ text: text.slice(marker.start, marker.end), source: marker.source });
    lastEnd = marker.end;
  }

  if (lastEnd < text.length) {
    parts.push({ text: text.slice(lastEnd) });
  }

  return (
    <div className="whitespace-pre-wrap text-sm text-gray-800 leading-relaxed">
      {parts.map((part, i) => {
        if (!part.source) {
          return <span key={i}>{part.text}</span>;
        }
        const colors = getDomainTypeColor(part.source.domain_type);
        return (
          <span
            key={i}
            className={`inline-flex items-center rounded-md px-1 py-0.5 text-xs font-medium ${colors.bg} ${colors.text}`}
            title={`${part.source.domain ?? 'Unknown'} (${part.source.domain_type ?? 'other'}) — ${part.source.is_cited ? 'Cited' : 'Retrieved only'}`}
            data-testid="citation-badge"
          >
            {part.text}
          </span>
        );
      })}
    </div>
  );
}

export function ResponseContent({ response }: ResponseContentProps) {
  const { query_job, raw_content, captured_at, latency_ms, token_usage, sources } = response;

  return (
    <div className="flex flex-col gap-4" data-testid="response-content">
      {/* Header: engine + model + timestamp */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <EngineBadge engine={query_job.engine} />
          {query_job.api_model && (
            <span className="text-xs text-gray-500">{query_job.api_model}</span>
          )}
          <CaptureModeBadge captureMode={response.capture_mode} />
          <ResponseRegionBadge
            regionCode={response.region_code}
            mechanism={response.region_mechanism}
            fidelity={response.region_fidelity}
          />
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-500">
          {latency_ms !== null && (
            <span title="Response latency">{latency_ms}ms</span>
          )}
          {token_usage && (
            <span title="Token usage">
              {token_usage.total ?? (token_usage.input ?? 0) + (token_usage.output ?? 0)} tokens
            </span>
          )}
          <time dateTime={captured_at} title={new Date(captured_at).toLocaleString()}>
            {new Date(captured_at).toLocaleString()}
          </time>
        </div>
      </div>

      {/* Capture provenance: web-search status (always rendered — this is what
          makes a "Sources (0)" response legible) + any extraction-gap warning. */}
      <div className="flex flex-col gap-2" data-testid="capture-provenance">
        <SearchStatusLine
          searchStatus={response.search_status}
          searchInvocations={response.search_invocations}
          sourcesReturnedByEngine={response.sources_returned_by_engine}
        />
        <ExtractionDiscrepancyWarning
          sourcesReturnedByEngine={response.sources_returned_by_engine}
          sourcesExtractedByBeacon={response.sources_extracted_by_beacon}
        />
      </div>

      {/* Prompt */}
      <div className="rounded-md bg-gray-50 p-3" data-testid="prompt-section">
        <p className="text-xs font-medium text-gray-500">Prompt</p>
        <p className="mt-1 text-sm text-gray-900">{query_job.rendered_prompt || 'No prompt available'}</p>
      </div>

      {/* Response text. Spec 094: Google AIO captures are structurally
          different from prose responses (lead + PAA + source snippets),
          so they route through AioResponseBody for an AIO-aware layout.
          Every other engine continues to render through the existing
          HighlightedText path — unchanged. */}
      <div data-testid="response-text">
        <p className="text-xs font-medium text-gray-500 mb-2">Response</p>
        {query_job.engine === 'google_aio' ? (
          <AioResponseBody
            rawContent={raw_content || ''}
            aioSources={(response.aio_sources ?? []) as AioSource[]}
            brandLocale={query_job.brand_locale ?? null}
          />
        ) : (
          <HighlightedText text={raw_content || 'No response content'} sources={sources} />
        )}
      </div>
    </div>
  );
}
